import {
  Box,
  Button,
  Input,
  InputGroup,
  Popover,
  Portal,
  Text
} from '@chakra-ui/react';
import { useState } from 'react';
import { LuCheck, LuChevronDown, LuPlus } from 'react-icons/lu';

import { setDropdownInteraction } from '@/utils/dropdownInteraction';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectWithNewOptionProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  onClick?: () => void;
  onAddNew?: () => void;
  placeholder?: string;
  width?: string;
  maxWidth?: string;
  isDisabled?: boolean;
  label?: string;
  required?: boolean;
  emptyStateMessage?: string;
  addNewButtonText?: string;
  hideBottomBorder?: boolean;
}

const SelectWithNewOption = ({
  onClick,
  options,
  value,
  onChange,
  onAddNew,
  placeholder = 'Select option',
  isDisabled = false,
  label,
  required = false,
  emptyStateMessage = 'No options available',
  addNewButtonText = 'Add new option',
  hideBottomBorder = false,
}: SelectWithNewOptionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(option => option.value === value);

  const handleOpenChange = (details: { open: boolean }) => {
    if (details.open && !isDisabled) {
      onClick?.();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (optionValue: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDropdownInteraction();
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleAddNew = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDropdownInteraction();
    if (onAddNew) {
      onAddNew();
      setIsOpen(false);
    }
  };

  return (
    <Box position="relative" width="100%">
      {label && (
        <Text fontSize={14} fontWeight={600} color="bg.default" mb={2}>
          {label}
          {' '}
          {required && <Text as="span" color="red.fg">*</Text>}
        </Text>
      )}
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
        <Popover.Trigger asChild>
          <Box
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            position="relative"
            width="100%"
          >
            <InputGroup>
              <Input
                w="100%"
                variant="flushed"
                placeholder={placeholder}
                value={selectedOption?.label || ''}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                readOnly
                disabled={isDisabled}
                borderBottom={hideBottomBorder ? 'none' : undefined}
                _focusVisible={hideBottomBorder ? { boxShadow: 'none' } : undefined}
                style={{
                  paddingInlineEnd: '40px'
                }}
              />
            </InputGroup>
            <Box
              position="absolute"
              right="12px"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              zIndex={1}
              display="flex"
              alignItems="center"
            >
              <LuChevronDown
                color="gray.400"
                size={16}
                style={{
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            </Box>
          </Box>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content
              width="var(--reference-width)"
              minWidth="var(--reference-width)"
              maxHeight={200}
              overflowY="auto"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
              backgroundColor="white"
              data-select-dropdown="true"
            >
              <Popover.CloseTrigger />
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.Body p={0}>
                {options.length > 0 ? (
                  options.map((option) => (
                    <Button
                      key={option.value}
                      variant="ghost"
                      width="100%"
                      justifyContent="space-between"
                      textAlign="left"
                      py={2}
                      px={3}
                      onClick={(e) => handleOptionSelect(option.value, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                      _hover={{ backgroundColor: 'gray.50' }}
                    >
                      {option.label}
                      {option.value === value && <LuCheck size={16} color="blue.600" />}
                    </Button>
                  ))
                ) : (
                  <Text p={3} color="gray.500" textAlign="center">
                    {emptyStateMessage}
                  </Text>
                )}

                {onAddNew && (
                  <>
                    <Box borderTop="1px solid" borderColor="gray.200" />
                    <Button
                      variant="ghost"
                      width="100%"
                      justifyContent="flex-start"
                      textAlign="left"
                      py={2}
                      px={3}
                      onClick={handleAddNew}
                      onMouseDown={(e) => e.stopPropagation()}
                      _hover={{ backgroundColor: 'gray.50' }}
                      color="bg.default"
                    >
                      <LuPlus size={16} />
                      {addNewButtonText}
                    </Button>
                  </>
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    </Box>
  );
};

export default SelectWithNewOption;

